import { AuthService } from '@uw/core/services/auth.service';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, NgForm, Validators, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Router } from '@angular/router';
import { BaseComponent } from '@uw/core/base.component';
import { CampaignService } from '@uw/core/services/campaign.service';
import { CampaignStateService } from '@uw/core/services/campaign-state.service';
import { AlertPageType, LoginAlert } from '@uw/core/enums/alert-pagetype.enum';
import { AlertService } from '@uw/core/services/alert.service';
import { Alert } from '@uw/core/models/alert.model';
import { CustomNavigationService } from '@uw/core/custom-navigation.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LayoutUtilsService } from '@uw/shared/services/layout-utils.service';
import { Subject, of, switchMap } from 'rxjs';
import { ProviderType, SSOCheckResponse } from '@uw/core/models/sso-check-response';
import { customEmailValidator } from '@uw/shared/validators/custom-email-validator';
import { LoginContentContainerComponent } from '../../../../shared/components/login-content-container/login-content-container.component';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { NgIf, NgStyle } from '@angular/common';
import { FocusDirective } from '../../../../shared/directives/focus/focus.directive';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { MatButton } from '@angular/material/button';
import { UwRouterLink } from '../../../../shared/directives/directives/uw-router-link.directive';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
    templateUrl: './standalone-forgot-password.component.html',
    styleUrls: ['./standalone-forgot-password.component.scss'],
    imports: [LoginContentContainerComponent, ReactiveFormsModule, MatFormField, NgIf, NgStyle, MatLabel, MatInput, FocusDirective, MatError, ButtonComponent, MatButton, UwRouterLink, TranslateModule]
})
export class StandaloneForgotPasswordComponent extends BaseComponent implements OnInit {

  forgotFormGrp!: UntypedFormGroup;
  matcher = new MyErrorStateMatcher();
  buttonLoading = false;
  errorMessage = '';
  campaignGuid: string;
  firstTimeUser = false;
  alerts: Alert[] = [];
  email: string;

  postSsoLoginTypeCheck$ = new Subject<SSOCheckResponse>();

  readonly currentLanguage = this.translateService.currentLang;
  
  constructor(private fb: UntypedFormBuilder,
    private authService: AuthService,
    private campaignService: CampaignService,
    private campaignStateService: CampaignStateService,
    private alertService: AlertService,
    protected customNavigationService: CustomNavigationService,
    public translateService: TranslateService,
    public router: Router,
    public layoutUtilsService: LayoutUtilsService
  ) {
    super();
    //this.menuService.showDonarSubMenu(true);
    this.createForm();
    const pageData = this.router.lastSuccessfulNavigation()?.extras.state || this.router.getCurrentNavigation()?.extras.state;
    this.firstTimeUser = pageData && pageData.firstTimeUser;

    this.email = pageData && pageData.email;

    if (this.email) {
      this.forgotFormGrp.get('email').setValue(this.email);
    }    
  }

  ngOnInit(): void {
    this.campaignGuid = this.campaignStateService.getSelectedCampaign()?.campaignGuid ?? '';
    this.alertService.fetchAlerts(AlertPageType.LOGIN).subscribe(
      alerts => {
        this.alerts = alerts;
      },
      _ => {
        this.alerts = [];
      }
    );

    this.postSsoLoginTypeCheck$.pipe(
      switchMap(ssoCheckResponse => {
        if (ssoCheckResponse.providerType.toLowerCase() == ProviderType.SSO.toLowerCase()) {

          // sso user
          this.authService.authenticateViaSSO(ssoCheckResponse.providerId, ssoCheckResponse.protocol);

        } else {
          // non sso user
          this.forgotPassword();
        }

        return of(true);
      })
    ).subscribe();
  }

  createForm() {
    this.forgotFormGrp = this.fb.group({
      email: [null, [Validators.required, customEmailValidator()]],
    });
  }

  onNoClick(): void {
    //this.dialogRef.close();
  }

  executeForgotPasswordByUserLoginType(): void {

    this.forgotFormGrp.markAllAsTouched();

    if (!this.forgotFormGrp.valid) {
      return
    }

    this.errorMessage = '';

    this.authService.checkForSSOLogin({ email: this.forgotFormGrp.get('email').value }).subscribe(
      ssoCheckResponse => {
        this.postSsoLoginTypeCheck$.next(ssoCheckResponse);
      },
      _ => {
        this.buttonLoading = false;
        this.errorMessage = this.translateService.instant('messages.forgot_password_error');
      }
    );
  }

  forgotPassword(): void {
    this.buttonLoading = true;
    const cGuid = this.campaignService.getCurrentCampaignGuid();
    this.authService.forgotPassword(this.forgotFormGrp.value.email, cGuid ?? '', this.firstTimeUser).subscribe(_ => {

      this.buttonLoading = false;
      this.showSuccess('forgot_password.success_msg', {email: this.forgotFormGrp.value.email});
      this.customNavigationService.navigate(['/donor', this.campaignGuid]);

    }, err => {
      this.buttonLoading = false;
      // this.errorMessage = err && err.message ? err.message : this.translateService.instant('messages.forgot_password_error')

      const alertMessage = this.alerts.find(al => al.name == LoginAlert.INVALID_USER);

      if (alertMessage) {
        this.errorMessage = (this.currentLanguage == 'en') ? alertMessage.contentXML: alertMessage.contentXML_fr;
      } else {
        this.errorMessage = this.translateService.instant('messages.forgot_password_error');
      }

    });
  }


}

